import React from 'react';
import { useController, useFormContext } from 'react-hook-form';

import { cn } from '@epoch-ui/lib/utils';
import { Input } from '@epoch-ui/components/Input';
import { SelectInput, SelectContent, SelectItem } from '@epoch-ui/components/Select';
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  CompositeFormMessage
} from '@epoch-ui/components/Form';
import { TimeOffsetUnit, TimeOffsetRelation } from 'constants/enums';

type TimeOffsetValue = {
  quantity: number | null;
  unit: TimeOffsetUnit;
  relation: TimeOffsetRelation;
};

type TimeOffsetOption = {
  value: TimeOffsetUnit | TimeOffsetRelation | (string & {});
  label: React.ReactNode;
};

type TimeOffsetConfig = {
  units: TimeOffsetOption[];
  relations: TimeOffsetOption[];
};

interface TimeOffsetFieldContextValue {
  id: string;
  name: string;
  disabled?: boolean;
  config: TimeOffsetConfig;
  defaultValue: TimeOffsetValue;
}

const TimeOffsetFieldContext = React.createContext<TimeOffsetFieldContextValue | null>(null);

const useTimeOffsetFieldContext = (): TimeOffsetFieldContextValue => {
  const context = React.useContext(TimeOffsetFieldContext);
  if (!context) {
    throw new Error('TimeOffsetField compound components must be used within TimeOffsetField');
  }
  return context;
};

export interface TimeOffsetFieldProps {
  /** Form field name for React Hook Form */
  name: string;
  /** Field label text or element */
  label?: React.ReactNode;
  /** Helper text displayed below label */
  description?: React.ReactNode;
  /** CSS classes for root container */
  className?: string;
  /** Disable entire field */
  disabled?: boolean;
  /** Configuration for units and relations */
  config: TimeOffsetConfig;
  /** Sub-components (Quantity, Unit, Relation) */
  children: React.ReactNode;
}

interface QuantityInputProps {
  /** Placeholder text for quantity input */
  placeholder?: string;
  /** CSS classes for quantity input */
  className?: string;
  /** Minimum value for quantity */
  min?: number;
}

interface UnitSelectProps {
  /** CSS classes for unit select */
  className?: string;
}

interface RelationSelectProps {
  /** CSS classes for relation select */
  className?: string;
}

/**
 * TimeOffsetFieldV2.Quantity - Quantity input sub-component
 */
const QuantityInputComponent = React.memo<QuantityInputProps>(
  ({ placeholder, className, min = 1 }) => {
    const { name, id: fieldId, disabled, defaultValue } = useTimeOffsetFieldContext();
    const {
      field,
      fieldState: { error }
    } = useController({ name });

    const fieldValue = field.value ?? defaultValue;
    const hasError = !!error?.message || !!(error as any)?.quantity?.message;

    const handleQuantityChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
      const quantity = Number(e.target.value);
      const newValue = {
        ...fieldValue,
        quantity: Number.isNaN(quantity) || quantity <= 0 ? null : quantity
      };
      field.onChange(newValue);
    };

    return (
      <FormControl className={cn('tw-mt-0', className)}>
        <Input
          id={`${fieldId}-quantity`}
          type="number"
          min={min}
          value={fieldValue.quantity ?? ''}
          placeholder={placeholder}
          disabled={disabled}
          error={hasError}
          onBlur={field.onBlur}
          onChange={handleQuantityChange}
          aria-describedby={error ? `${fieldId}-error` : undefined}
        />
      </FormControl>
    );
  }
);

QuantityInputComponent.displayName = 'TimeOffsetFieldV2.Quantity';

/**
 * TimeOffsetFieldV2.Unit - Unit selection sub-component
 */
const UnitSelectComponent = React.memo<UnitSelectProps>(({ className }) => {
  const { name, id: fieldId, disabled, config, defaultValue } = useTimeOffsetFieldContext();
  const {
    field,
    fieldState: { error }
  } = useController({ name });

  const fieldValue = field.value ?? defaultValue;
  const hasError = !!error?.message || !!(error as any)?.unit?.message;

  const handleUnitChange = (value: string): void => {
    const newValue = {
      ...fieldValue,
      unit: value as TimeOffsetUnit
    };
    field.onChange(newValue);
  };

  return (
    <FormControl className={cn('tw-mt-0', className)}>
      <SelectInput
        value={fieldValue.unit}
        disabled={disabled}
        error={hasError}
        onValueChange={handleUnitChange}
        aria-describedby={error ? `${fieldId}-error` : undefined}
      >
        <SelectContent>
          {config.units.map(({ value, label: unitLabel }) => (
            <SelectItem key={value} value={value}>
              {unitLabel}
            </SelectItem>
          ))}
        </SelectContent>
      </SelectInput>
    </FormControl>
  );
});

UnitSelectComponent.displayName = 'TimeOffsetFieldV2.Unit';

/**
 * TimeOffsetFieldV2.Relation - Relation selection sub-component
 */
const RelationSelectComponent = React.memo<RelationSelectProps>(({ className }) => {
  const { name, id: fieldId, disabled, config, defaultValue } = useTimeOffsetFieldContext();
  const {
    field,
    fieldState: { error }
  } = useController({ name });

  const fieldValue = field.value ?? defaultValue;
  const hasError = !!error?.message || !!(error as any)?.relation?.message;

  const handleRelationChange = (value: string): void => {
    const newValue = {
      ...fieldValue,
      relation: value as TimeOffsetRelation
    };
    field.onChange(newValue);
  };

  return (
    <FormControl className={cn('tw-mt-0', className)}>
      <SelectInput
        value={fieldValue.relation}
        disabled={disabled}
        error={hasError}
        onValueChange={handleRelationChange}
        aria-describedby={error ? `${fieldId}-error` : undefined}
      >
        <SelectContent>
          {config.relations.map(({ value, label: relationLabel }) => (
            <SelectItem key={value} value={value}>
              {relationLabel}
            </SelectItem>
          ))}
        </SelectContent>
      </SelectInput>
    </FormControl>
  );
});

RelationSelectComponent.displayName = 'TimeOffsetFieldV2.Relation';

// Root component
const TimeOffsetFieldRoot = React.forwardRef<HTMLDivElement, TimeOffsetFieldProps>(
  ({ name, label, description, className, disabled, config, children }, ref) => {
    const { control } = useFormContext();
    const fieldId = React.useId();

    const defaultValue: TimeOffsetValue = React.useMemo(
      () => ({
        quantity: 1,
        unit: (config.units[0]?.value as TimeOffsetUnit) ?? TimeOffsetUnit.MINUTES,
        relation: (config.relations[0]?.value as TimeOffsetRelation) ?? TimeOffsetRelation.BEFORE
      }),
      [config.units, config.relations]
    );

    const contextValue: TimeOffsetFieldContextValue = React.useMemo(
      () => ({
        id: fieldId,
        name,
        disabled,
        config,
        defaultValue
      }),
      [name, fieldId, disabled, config, defaultValue]
    );

    return (
      <FormField
        control={control}
        name={name}
        render={() => {
          return (
            <TimeOffsetFieldContext.Provider value={contextValue}>
              <FormItem className={className} ref={ref}>
                {label && (
                  <FormLabel
                    id={`${fieldId}-label`}
                    className={cn('tw-mb-2', description && 'tw-mb-1', disabled && 'tw-opacity-40')}
                    htmlFor={`${fieldId}-quantity`}
                  >
                    {label}
                  </FormLabel>
                )}
                {description && (
                  <FormDescription className="tw-mb-2">{description}</FormDescription>
                )}
                <div
                  className="tw-flex tw-w-full tw-items-center tw-gap-3"
                  role="group"
                  aria-labelledby={label ? `${fieldId}-label` : undefined}
                >
                  {children}
                </div>
                <CompositeFormMessage
                  id={`${fieldId}-error`}
                  priority={['quantity', 'unit', 'relation']}
                />
              </FormItem>
            </TimeOffsetFieldContext.Provider>
          );
        }}
      />
    );
  }
);

TimeOffsetFieldRoot.displayName = 'TimeOffsetFieldV2';

/**
 * TimeOffsetFieldV2 - A compound form field component for time offset selection
 *
 * @description
 * A flexible, accessible time offset picker built with compound component pattern.
 * Integrates with React Hook Form and supports nested validation errors.
 *
 * @example
 * Basic usage (all inputs on same line):
 * ```tsx
 * <TimeOffsetFieldV2 name="schedule" label="Schedule" config={config}>
 *   <TimeOffsetFieldV2.Quantity className="tw-w-1/3" />
 *   <TimeOffsetFieldV2.Unit />
 *   <TimeOffsetFieldV2.Relation />
 * </TimeOffsetFieldV2>
 * ```
 *
 * @example
 * Custom layout (relation on separate line):
 * ```tsx
 * <TimeOffsetFieldV2 name="schedule" label="Schedule" config={config}>
 *   <div className="tw-flex tw-gap-3">
 *     <TimeOffsetFieldV2.Quantity className="tw-w-1/3" />
 *     <TimeOffsetFieldV2.Unit />
 *   </div>
 *   <TimeOffsetFieldV2.Relation />
 * </TimeOffsetFieldV2>
 * ```
 *
 * @example
 * With custom styling:
 * ```tsx
 * <TimeOffsetFieldV2 name="schedule" label="Schedule" config={config}>
 *   <TimeOffsetFieldV2.Quantity
 *     className="tw-w-20"
 *     placeholder="Qty"
 *     min={1}
 *   />
 *   <TimeOffsetFieldV2.Unit className="tw-min-w-[100px]" />
 *   <TimeOffsetFieldV2.Relation className="tw-min-w-[150px]" />
 * </TimeOffsetFieldV2>
 * ```

 * @requires React Hook Form context via useFormContext()
 */
const TimeOffsetFieldV2 = Object.assign(TimeOffsetFieldRoot, {
  Quantity: QuantityInputComponent,
  Unit: UnitSelectComponent,
  Relation: RelationSelectComponent
});

export { TimeOffsetFieldV2, type TimeOffsetConfig };
